import { ChangeTag, SalesDocumentType, SalesDocument, DocumentAttachment, SalesDocumentConnections, SalesDocumentPosition, SalesDocumentPositionBasePrices, SalesDocumentPositionPrices, IccDocument, IccDocumentPosition, ComplaintsDocumentType, Complaint, DocumentIntegrityService } from "@icc/b2b/shared";

export abstract class DocumentManagerService {
    abstract getDocument(documentId: string): Promise<IccDocument>;
    abstract getConnectionsForDocument(documentId: string): Promise<SalesDocumentConnections[string]>;
    abstract getBuyDocuments(documentId: string): Promise<SalesDocument[]>;
    abstract getBuyDocumentsForAllDocuments(documentIds: string[]): Promise<Map<string, SalesDocument[]>>;
    abstract getSubdocuments(rootId: string, type: SalesDocumentType | ComplaintsDocumentType): Promise<SalesDocument[] | Complaint[]>;
    abstract getBuyPositionsMap(documentId: string, all?: boolean): Promise<Map<string, SalesDocumentPosition>>;
    abstract getDocumentPosition(documentId: string, all?: boolean): Promise<IccDocumentPosition[]>;
    abstract getDocumentAttachments(documentId: string): Promise<DocumentAttachment[]>;

    abstract getSellerUserCode(salesDocument: SalesDocument): Promise<string>;

    abstract addDocument(document: SalesDocument): void;
    abstract addPosition(
        position: IccDocumentPosition,
        document: SalesDocument,
        onlySales?: boolean,
    ):void;
    abstract savePosition(
        positionId: string,
        documentId: string,
        position: Partial<
            Omit<
                SalesDocumentPosition,
                keyof SalesDocumentPositionPrices | keyof SalesDocumentPositionBasePrices
            >
        >,
        onlySales?: boolean,
    ): void;
    abstract savePositionPrices(
        positionId: string,
        documentId: string,
        position: SalesDocumentPositionPrices,
        buy?: boolean,
    );
    abstract addAttachment(attachment: DocumentAttachment): void;
    abstract saveDocument(documentId: string, document: Partial<SalesDocument>, onlySales?: boolean): void;
    abstract joinDocuments(salesDocumentId: SalesDocument['uuid'], buyDocumentId: SalesDocument['uuid']): void;
    abstract joinPositions(
        salesDocumentId: SalesDocument['uuid'],
        buyDocumentId: SalesDocument['uuid'],
        salesPositionId: string,
        buyPositionId: string
    ): void;
    abstract joinAttachments(
        salesDocumentId: SalesDocument['uuid'],
        buyDocumentId: SalesDocument['uuid'],
        salesAttachmentId: string,
        buyAttachmentId: string
    ): void;
    abstract detachDocuments(salesDocumentId: SalesDocument['uuid'], buyDocumentId: SalesDocument['uuid']): void;
    abstract tagChanges(documentId: string, changeTag: ChangeTag): void;
    abstract findLatestOrderId(document: SalesDocument): Promise<string | null>;
    async checkIntegrity(document: SalesDocument, buyDocuments?: SalesDocument[]) {
        let integral = true;
        buyDocuments = buyDocuments?.length ? buyDocuments : await this.getBuyDocuments(document.uuid);
        const positions = await this.getDocumentPosition(document.uuid, true) as SalesDocumentPosition[];
        const buyPositionsMap = await this.getBuyPositionsMap(document.uuid, true);
        const buyPositions: SalesDocumentPosition[] = [];
        const salesPositionsSet = new Set(positions.map((el) => el.uuid));
        if (buyDocuments.length > 0) {
            for (const buyDocument of buyDocuments) {
                buyPositions.push(...(await this.getDocumentPosition(buyDocument.uuid, true)) as SalesDocumentPosition[]);
                const differences = DocumentIntegrityService.checkDocumentsIntegrity(
                    document,
                    buyDocument
                );
                integral = integral && differences.length === 0;
            }
            for (const position of positions) {
                if (buyPositionsMap.has(position.uuid)) {
                    const positionDifferences = DocumentIntegrityService.checkPositionsIntegrity(
                        position,
                        buyPositionsMap.get(position.uuid),
                        buyPositionsMap,
                    );
                    integral = integral && positionDifferences.length === 0;
                } else if (position.type !== 'additional') {
                    integral = false;
                    break;
                }
            }
            const salesPositionsMap: Map<string, boolean> = new Map();
            buyPositionsMap.forEach((value, key) => {
                if (value && salesPositionsSet.has(key)) {
                    salesPositionsMap.set(value.uuid, true);
                }
            });
            for (const buyPosition of buyPositions) {
                if (!salesPositionsMap.has(buyPosition.uuid)) {
                    integral = false;
                    break;
                }
            }
        }
        return integral;
    }
}
